import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DealsCardSkeleton from './DealsCardSkeleton';

const getAccordion = idSuffix => {
	const ariaControlsId = `panel1a-content-${idSuffix}`;
	const accordionSummaryId = `panel1a-header-${idSuffix}`;
	const accordionDetailsId = `panel1a-details-${idSuffix}`;
	return (
		<Stack pb={1}>
			<Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls={ariaControlsId}
					id={accordionSummaryId}
				>
					<Skeleton width="40%" />
				</AccordionSummary>
				<AccordionDetails id={accordionDetailsId}>
					<Skeleton />
				</AccordionDetails>
			</Accordion>
		</Stack>
	);
};

export default function DealDetailsPageSkeleton({ dealType }) {
	return (
		<Grid container component="article">
			<Grid item xs={12} md={8} pr={{ md: 2 }}>
				<Grid>
					<Stack mb={2} data-testid="skeletonLoadingForDealTopLayout">
						<Stack mb={1}>
							<Skeleton variant="rectangular" width="20%" height={50} />
						</Stack>
						<Stack>
							<Skeleton variant="text" width="50%" />
						</Stack>
						<Stack>
							<Skeleton variant="text" />
							<Skeleton variant="text" width="80%" />
						</Stack>
						<Stack>
							<Skeleton variant="rectangular" height={400} />
						</Stack>
					</Stack>
				</Grid>
				<Grid>
					<Stack
						spacing={2}
						data-testid="skeletonLoadingDescriptionDealTitleImageDescription"
					>
						<Stack>
							<Skeleton variant="text" width="20%" height={40} />
						</Stack>
						<Stack data-testid="skeletonLoadingDescription">
							<Skeleton variant="text" />
							<Skeleton variant="text" />
							<Skeleton variant="text" width="80%" />
						</Stack>
					</Stack>
				</Grid>
				{dealType !== 'vendor' && (
					<Grid>
						<Stack
							spacing={2}
							data-testid="skeletonLoadingImageDealTitleImageDescription"
						>
							<Stack>
								<Skeleton variant="text" width="20%" height={40} />
							</Stack>
							<Stack data-testid="skeletonLoadingImage">
								<Skeleton variant="rectangular" height={200} />
							</Stack>
						</Stack>
					</Grid>
				)}
				{dealType === 'afd' && (
					<Grid>
						<Skeleton data-testid="skeletonLoadingForDisclaimer" />
					</Grid>
				)}
				{dealType === 'specialty' && (
					<>
						<Grid mt={3}>
							<Typography variant="h5" component="h2" gutterBottom>
								<Skeleton width="20%" />
							</Typography>
							<Stack>{getAccordion('questions')}</Stack>
						</Grid>
						<Grid mt={3}>
							<Typography variant="h5" component="h2" gutterBottom>
								Credits & References
							</Typography>
							<Stack>{getAccordion('credit&references')}</Stack>
						</Grid>
					</>
				)}
			</Grid>
			<Grid item xs={12} md={4} mt={{ md: 8, sm: 3 }}>
				<Paper variant="outlined" data-testid="skeletonLoadingForHowToSection">
					<Stack spacing={4} p={7} pl={4} alignItems="center">
						<Stack>
							<Skeleton
								variant="rectangular"
								height={20}
								width={150}
								data-testid="skeltonLoading"
							/>
						</Stack>
						<Stack spacing={1} alignItems="center">
							<Skeleton variant="circular" width={40} height={40} />
							<Skeleton variant="text" height={20} width={100} />
							<Skeleton variant="text" height={20} width={150} />
						</Stack>
						<Stack spacing={1} alignItems="center">
							<Skeleton variant="circular" width={40} height={40} />
							<Skeleton variant="text" height={20} width={100} />
							<Skeleton variant="text" height={20} width={150} />
						</Stack>
						<Skeleton variant="rectangular" height={50} width={150} />
					</Stack>
				</Paper>
			</Grid>
			<Grid container mt={3}>
				<Typography variant="h5" component="h2" gutterBottom>
					Recommended Deals
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<DealsCardSkeleton />
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<DealsCardSkeleton />
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<DealsCardSkeleton />
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<DealsCardSkeleton />
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}

DealDetailsPageSkeleton.propTypes = {
	dealType: PropTypes.string.isRequired,
};
